import * as React from "react";

import { PresentationFacet, ResponseFacetValue } from "../../model";

import {
  useProductListController_forFiltering,
  useProductListPresentationModelFacets,
  useProductListState,
} from "../product-list-provider";
import { usePlpAnalytics } from "../plp-analytics-provider";

interface PlpPresenterForTheFilterMenu_MobileConsumerInterface {
  shouldShowTheFilterMenu_onMobile: boolean;

  handleShowTheFilterMenu_onMobile: () => void;
  handleHideTheFilterMenu_onMobile: () => void;

  whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile?: PresentationFacet;
  handleNavigatingToAFilterSubMenu_onMobile: (
    facet?: PresentationFacet
  ) => void;
  handleNavigatingBackFromAFilterSubmenu_onMobile: () => void;

  handleActionFilterOption_onMobile: (option?: ResponseFacetValue) => void;
  doesTheExclusivelyOpenFilterSubMenu_onMobile_haveOptionsSelected: boolean;

  handleClearFilters_InTheExclusivelyOpenFilterSubMenu_onMobile: () => void;
}

const defaultMobileValue =
  {} as unknown as PlpPresenterForTheFilterMenu_MobileConsumerInterface;
const PlpPresenterForTheFilterMenu_MobileContext =
  React.createContext(defaultMobileValue);

export const PlpPresenterForTheFilterMenu_MobileProvider: React.FunctionComponent =
  (props) => {
    const { selectedOptions } = useProductListState();
    const { presentationFacets } = useProductListPresentationModelFacets();
    const {
      isOptionSelected: productListController_forFiltering_isOptionSelected,
      handleActionFilterOption:
        productListController_forFiltering_handleActionFilterOption,
      handleRemoveSelectedOptionsInBulk:
        productListController_forFiltering_handleRemoveSelectedOptionsInBulk,
    } = useProductListController_forFiltering();

    const { analytics } = usePlpAnalytics();

    const [
      shouldShowTheFilterMenu_onMobile,
      setShouldShowTheFilterMenu_onMobile,
    ] = React.useState(false);
    const [
      whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
      setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
    ] = React.useState<PresentationFacet | undefined>();

    const whatIsTheExclusivelyOpenFilterSubMenus_selectedOptions_onMobile =
      React.useMemo(() => {
        if (!whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile) {
          return [];
        }

        const viewingFilterFacetName =
          whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile.key;
        if (!viewingFilterFacetName) {
          return [];
        }

        const viewingFilterSelectedOptions = selectedOptions.filter(
          ({ facetName }) => facetName === viewingFilterFacetName
        );

        return viewingFilterSelectedOptions;
      }, [
        whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
        selectedOptions,
      ]);

    const doesTheExclusivelyOpenFilterSubMenu_onMobile_haveOptionsSelected =
      React.useMemo(() => {
        const viewingFilterSelectedOptions =
          whatIsTheExclusivelyOpenFilterSubMenus_selectedOptions_onMobile;

        return viewingFilterSelectedOptions.length > 0;
      }, [
        whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
        selectedOptions,
        whatIsTheExclusivelyOpenFilterSubMenus_selectedOptions_onMobile,
      ]);

    React.useEffect(() => {
      if (whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile) {
        let matchingFacet: PresentationFacet | undefined =
          presentationFacets.find(
            ({ key }) =>
              key === whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile.key
          );

        if (!matchingFacet?.hasSubMenu) {
          handleNavigatingBackFromAFilterSubmenu_onMobile();
        } else {
          setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile(
            matchingFacet
          );
        }
      }
    }, [
      presentationFacets,
      whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
    ]);

    const handleNavigatingBackFromAFilterSubmenu_onMobile =
      React.useCallback(() => {
        setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile(undefined);
      }, []);

    const handleShowTheFilterMenu_onMobile = React.useCallback(() => {
      setShouldShowTheFilterMenu_onMobile(true);
    }, []);
    const handleHideTheFilterMenu_onMobile = React.useCallback(() => {
      handleNavigatingBackFromAFilterSubmenu_onMobile();
      setShouldShowTheFilterMenu_onMobile(false);
    }, [handleNavigatingBackFromAFilterSubmenu_onMobile]);

    const handleNavigatingToAFilterSubMenu_onMobile = React.useCallback(
      (facet?: PresentationFacet) => {
        setWhatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile(facet);
      },
      []
    );
    const handleActionFilterOption_onMobile = React.useCallback(
      (option?: ResponseFacetValue) => {
        if (
          !whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile ||
          !option
        ) {
          return;
        }

        const wasTheOptionOnWhenTheUserClickedOnIt =
          productListController_forFiltering_isOptionSelected(
            whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
            option
          );

        productListController_forFiltering_handleActionFilterOption(
          whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
          option
        );

        analytics.clickActionOnFacet({
          facet: whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
          option: option,
          wasTheOptionOnWhenTheUserClickedOnIt,
          isMobile: true,
        });
      },
      [
        whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
        productListController_forFiltering_isOptionSelected,
        productListController_forFiltering_handleActionFilterOption,
        analytics,
      ]
    );

    const handleClearFilters_InTheExclusivelyOpenFilterSubMenu_onMobile =
      React.useCallback(() => {
        const viewingFilterSelectedOptions =
          whatIsTheExclusivelyOpenFilterSubMenus_selectedOptions_onMobile;
        productListController_forFiltering_handleRemoveSelectedOptionsInBulk(
          viewingFilterSelectedOptions
        );
      }, [
        whatIsTheExclusivelyOpenFilterSubMenus_selectedOptions_onMobile,
        productListController_forFiltering_handleRemoveSelectedOptionsInBulk,
      ]);

    const value: PlpPresenterForTheFilterMenu_MobileConsumerInterface =
      React.useMemo(
        () => ({
          shouldShowTheFilterMenu_onMobile,
          handleShowTheFilterMenu_onMobile,
          handleHideTheFilterMenu_onMobile,

          whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
          handleNavigatingToAFilterSubMenu_onMobile,
          handleNavigatingBackFromAFilterSubmenu_onMobile,

          handleActionFilterOption_onMobile,
          doesTheExclusivelyOpenFilterSubMenu_onMobile_haveOptionsSelected,

          handleClearFilters_InTheExclusivelyOpenFilterSubMenu_onMobile,
        }),
        [
          shouldShowTheFilterMenu_onMobile,
          handleShowTheFilterMenu_onMobile,
          handleHideTheFilterMenu_onMobile,

          whatIsTheExclusivelyOpenFilterSubMenuRightNow_onMobile,
          handleNavigatingToAFilterSubMenu_onMobile,
          handleNavigatingBackFromAFilterSubmenu_onMobile,

          handleActionFilterOption_onMobile,
          doesTheExclusivelyOpenFilterSubMenu_onMobile_haveOptionsSelected,

          handleClearFilters_InTheExclusivelyOpenFilterSubMenu_onMobile,
        ]
      );
    return (
      <PlpPresenterForTheFilterMenu_MobileContext.Provider value={value}>
        {props.children || []}
      </PlpPresenterForTheFilterMenu_MobileContext.Provider>
    );
  };

export const usePlpPresenterForTheFilterMenu_Mobile = () => {
  const value = React.useContext(PlpPresenterForTheFilterMenu_MobileContext);
  if (value === defaultMobileValue) {
    throw new Error(
      "usePlpPresenterForTheFilterMenu_Mobile must be used within a PlpPresenterForTheFilterMenu_MobileProvider"
    );
  }
  return value;
};
